import type {
  ProjectValidationLibraryAuthorityContext,
} from "../library-composition/types"

export type TargetPlatform = "web"
export type ActionRole =
  | "primary"
  | "secondary"
  | "recovery"
  | "destructive"
export type SlotCardinality = "zero-or-one" | "exactly-one" | "many"
export type AuthorityStatus =
  | "candidate"
  | "consumer-verified"
  | "project-validation"
  | "retired"

export const SEMANTIC_CONTENT_KEYS = [
  "title",
  "description",
  "label",
  "location",
  "pendingLabel",
  "successLabel",
  "errorLabel",
] as const

export type SemanticContentKey = (typeof SEMANTIC_CONTENT_KEYS)[number]
export type SemanticContent = Partial<
  Record<SemanticContentKey, string | number | boolean>
>

export interface PlannedAction {
  role: ActionRole
  capabilityRef: string
  slotRef: string
}

export interface SemanticNodePlan {
  nodeId: string
  semanticRole: string
  regionRef: string
  content: SemanticContent
  visibleIn: string[]
  action?: PlannedAction
}

export interface ApplicationCompositionPlan {
  schemaVersion: "cogo.application-composition-plan.v1"
  planId: string
  applicationCoreRef: string
  target: TargetPlatform
  locale: string
  recipeRef: string
  states: string[]
  semanticNodes: SemanticNodePlan[]
}

export interface ActionSlotContract {
  slotId: string
  acceptsRoles: ActionRole[]
  capabilityRefs: string[]
  cardinality: SlotCardinality
  exclusiveGroup: string
}

export interface ComponentContract {
  componentId: string
  semanticRole: string
  rendererRef: string
  tokenRefs: string[]
  actionSlots: ActionSlotContract[]
}

export interface RecipeRegion {
  regionId: string
  order: number
  acceptsSemanticRoles: string[]
  maxPrimaryActions: number
}

export interface CompositionRecipe {
  recipeId: string
  regions: RecipeRegion[]
}

interface DesignCompositionReleaseBase {
  schemaVersion: "cogo.design-composition-release.v1"
  releaseId: string
  target: TargetPlatform
  sourceRefs: string[]
  tokenRefs: string[]
  components: ComponentContract[]
  recipes: CompositionRecipe[]
  actionTokenBindings: Record<ActionRole, string>
}

export interface CanonicalDesignCompositionRelease
  extends DesignCompositionReleaseBase {
  authorityStatus: Exclude<AuthorityStatus, "project-validation">
  projectValidationBinding?: never
}

export interface ProjectValidationDesignCompositionRelease
  extends DesignCompositionReleaseBase {
  authorityStatus: "project-validation"
  projectValidationBinding: ProjectValidationDesignReleaseBinding
}

export type DesignCompositionRelease =
  | CanonicalDesignCompositionRelease
  | ProjectValidationDesignCompositionRelease

export interface ProjectValidationDesignReleaseBinding {
  aggregationScope: "wonbanking-library-validation"
  sliceId: "account-selection-bottom-sheet"
  sourceMaterializationReceiptRef: string
  sourceMaterializationReceiptHash: `sha256:${string}`
  observedRoleProjectionHash: `sha256:${string}`
  contractRef: "cogo:project-validation-contract/wonbanking-account-selection-v1"
  contractHash: `sha256:${string}`
  processLocal: true
  persistenceAllowed: false
  promotionAllowed: false
}

export interface ProjectValidationCompositionAuthority {
  authorityMode: "project-validation"
  authorityContext: ProjectValidationLibraryAuthorityContext
  authorityContextHash: `sha256:${string}`
  applicationCoreRef: "cogo:project-validation-application-core/wonbanking-account-selection-v1"
  designCompositionReleaseRef: string
  designCompositionReleaseHash: `sha256:${string}`
  sourceMaterializationReceiptRef: string
  sourceMaterializationReceiptHash: `sha256:${string}`
  contentHash: `sha256:${string}`
}

export type DesignGapCode =
  | "plan-schema-invalid"
  | "release-schema-invalid"
  | "plan-state-invalid"
  | "semantic-node-missing"
  | "node-visibility-invalid"
  | "duplicate-node-id"
  | "release-not-consumer-verified"
  | "project-validation-authority-invalid"
  | "target-mismatch"
  | "recipe-missing"
  | "recipe-authority-ambiguous"
  | "region-missing"
  | "region-authority-ambiguous"
  | "semantic-role-not-admitted"
  | "component-missing"
  | "component-authority-ambiguous"
  | "token-missing"
  | "action-slot-missing"
  | "action-slot-authority-ambiguous"
  | "action-role-not-admitted"
  | "capability-not-admitted"
  | "exclusive-action-conflict"
  | "action-slot-cardinality"
  | "primary-action-cardinality"
  | "raw-presentation-forbidden"
  | "action-semantic-mismatch"

export interface DesignGap {
  code: DesignGapCode
  path: string
  message: string
  evidenceRefs: string[]
}

export interface CompiledCompositionNode {
  nodeId: string
  componentRef: string
  rendererRef: string
  regionRef: string
  content: SemanticContent
  visibleIn: string[]
  tokenRefs: string[]
  action?: PlannedAction & {
    tokenRef: string
    exclusiveGroup: string
  }
  provenance: {
    planRef: string
    releaseRef: string
    recipeRef: string
  }
}

export interface CompiledApplicationComposition {
  schemaVersion: "cogo.compiled-application-composition.v1"
  planId: string
  applicationCoreRef: string
  target: TargetPlatform
  locale: string
  designReleaseRef: string
  recipeRef: string
  states: string[]
  nodes: CompiledCompositionNode[]
  generationTrace: {
    mode: "deterministic-design-composition"
    rawPresentationAuthoredByModel: false
    genericFallbackUsed: false
    generatedOutputAdmissionAllowed: false
    projectValidationAuthority?: ProjectValidationCompositionAuthority
  }
}

export type CompileCompositionResult =
  | { ok: true; artifact: CompiledApplicationComposition }
  | { ok: false; gaps: DesignGap[] }

export type CompositionInputAdmissionResult =
  | {
      ok: true
      input: {
        readonly plan: ApplicationCompositionPlan
        readonly release: DesignCompositionRelease
        readonly projectValidationAuthority?: ProjectValidationCompositionAuthority
      }
    }
  | { ok: false; gaps: DesignGap[] }

export function assertNever(value: never): never {
  throw new Error(`Unexpected value: ${String(value)}`)
}
